import React, { useState, useEffect, useContext } from 'react';
import Breadcrumbs from '@/components/breadcrumbs';
import Router from 'next/router';
import { useRouter } from 'next/router';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import BillingService from '@/services/billing-service';
import SubscriptionContext from '@/utils/subscription-context';
import EmployeeService from '@/services/employee/employee-service';
import DocumentService from '@/services/document/document-service';
import NewMessageForm from "@/PageComponents/Message/Communication/NewMessageForm";
import {Box, Card, Space, Text} from "@mantine/core";
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'
import constants from "@/utils/constants";
import JobsTable from "@/PageComponents/Table/Component Tables/JobsTable";
import RecurringJobsTable from "@/PageComponents/Table/Component Tables/RecurringJobsTable";
import ProjectsTable from "@/PageComponents/Table/Component Tables/ProjectsTable";
import SCMessageBarContext from "@/utils/contexts/sc-message-bar-context";

function NewCommunicationPage() {

  const router = useRouter();
  const { id, method, moduleCode, attachQuote, attachInvoice, templateID, attachPurchaseOrder } = router.query;
 // TODO(tanstack-router): verify router.query → useParams/useSearch

  const messageBarContext = useContext(SCMessageBarContext);

  const subscriptionContext = useContext(SubscriptionContext);

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

  const getAccessStatus = async () => {
    const [subscriptionInfo, message] = await BillingService.getSubcriptionInfo(subscriptionContext);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  };

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  const getItemUrl = () => {
    switch (parseInt((moduleCode as any))) {
      case Enums.Module.Customer: return { itemUrl: '/Customer/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.JobCard: return { itemUrl: '/Job/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Asset: return { itemUrl: '/Product?id=', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Query: return { itemUrl: '/Query/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Quote: return { itemUrl: '/Quote/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Invoice: return { itemUrl: '/Invoice/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Project: return { itemUrl: '/Project/', isSupplier: false, isPurchaseOrder: false };
      case Enums.Module.Supplier: return { itemUrl: '/Supplier/', isSupplier: true, isPurchaseOrder: false };
      case Enums.Module.PurchaseOrder: return { itemUrl: '/PurchaseOrder/', isSupplier: true, isPurchaseOrder: true };
      default: return { itemUrl: '', isSupplier: false, isPurchaseOrder: false };
    }
  };

  const { itemUrl, isSupplier, isPurchaseOrder } = moduleCode ? getItemUrl() : { itemUrl: '', isSupplier: false, isPurchaseOrder: false };

  const { data: item } = useQuery(
    ['new-comm-item', itemUrl, id],
    () => Fetch.get({ url: itemUrl + id }),
    { enabled: !!itemUrl && !!id }
  );

  const { data: customer } = useQuery(
    ['new-comm-customer', item?.CustomerID, moduleCode, isSupplier],
    async () => {
      if (isSupplier) return null;
      if (!moduleCode || parseInt((moduleCode as any)) === 0) return item;
      return Fetch.get({ url: '/Customer/' + item.CustomerID });
    },
    { enabled: !!item && !isSupplier }
  );

  const { data: supplier } = useQuery(
    ['new-comm-supplier', item?.SupplierID, isPurchaseOrder, isSupplier],
    async () => {
      if (!isSupplier) return null;
      if (isPurchaseOrder) return Fetch.get({ url: `/Supplier/${item.SupplierID}` });
      return item;
    },
    { enabled: !!item && isSupplier }
  );

  const { data: templatesData } = useQuery(
    ['new-comm-templates', moduleCode],
    () => Fetch.post({
      url: '/Template/GetTemplates',
      params: {
        ModuleList: [Enums.getEnumStringValue(Enums.Module, parseInt((moduleCode as any)))],
        PageSize: 1000
      }
    }),
    { enabled: !!moduleCode }
  );

  const { data: templateData } = useQuery(
    ['new-comm-template', templateID],
    () => Fetch.get({ url: '/Template', params: { id: templateID } }),
    { enabled: !!templateID }
  );

  const { data: employeesData } = useQuery(
    ['new-comm-employees', item?.StoreID],
    () => EmployeeService.getEmployees(item.StoreID),
    { enabled: !!item }
  );

  const { data: legacyDocuments } = useQuery(
    ['new-comm-legacy-documents'],
    () => DocumentService.getUseLegacyDocuments()
  );

  const { data: documentDefinition } = useQuery(
    ['new-comm-document-definition'],
    () => DocumentService.getDocumentDefinition(),
    { enabled: legacyDocuments === false }
  );

  const documentDefinitionMetaData = documentDefinition?.MetaData !== undefined
    ? JSON.parse(documentDefinition.MetaData)
    : null;

  const isReady = !!item && !!templatesData && !!employeesData && (isSupplier ? !!supplier : !!customer);

  if (!isReady) return null;

  return (
    <Box>
      <Box
          my={'md'}
          px={'xl'}
      >
        <Breadcrumbs currPage={{ text: 'Messages', link: '', type: 'edit' }} />
      </Box>

      <Box
          h={`calc(100vh - 90px${((messageBarContext as any)?.isActive ? ` - ${constants.messageBarMargin}px` : "")})`}
          style={(theme: any) => ({
            backgroundColor: theme.colors.gray[1],
            overflow: 'auto'
          })}
          p={'var(--mantine-spacing-xs)'}
      >

        <NewMessageForm
            supplier={supplier}
            customer={customer}
            item={item}
            itemId={id}
            itemUrl={itemUrl}
            method={method}
            moduleCode={moduleCode}
            templates={templatesData.Results}
            attachQuote={attachQuote}
            attachInvoice={attachInvoice}
            attachPurchaseOrder={attachPurchaseOrder}
            template={templateData ?? null}
            employees={employeesData.Results}
            documentDefinitionMetaData={documentDefinitionMetaData}
        />

        <Space h={60}/>

      </Box>

    </Box>
  );
}

export default NewCommunicationPage;

export const Route = createFileRoute('/_authenticated/new-communication/$id')({ component: NewCommunicationPage })
